import React, { Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider } from '@tanstack/react-router';
import { ThemeProvider } from '@/components/theme-provider';
import { createAppRouter } from '@/routeTree';
import '@ygoargentina/ui/styles/globals.css';
import 'flag-icons/css/flag-icons.min.css';
import AuthProvider, { useAuth } from '@/contexts/Auth';
import { OrderSocketProvider } from '@/contexts/OrderSocket';
import Config from './config';
import { useUserStore } from './store/users';
import { BaseLoader } from '@ygoargentina/ui/components/loader';
import { QueryClientProvider } from '@tanstack/react-query';
import FolderCollectionProvider from '@/components/collection-provider';
import {
  getQueryClient,
  setUnauthorizedHandler,
} from '@ygoargentina/sdk/api/query-client';
import { authService } from '@ygoargentina/sdk/api/auth-service';
import { Toaster } from '@ygoargentina/ui/components/sonner';
import { registerSonnerNotifier } from '@ygoargentina/ui/lib/notifier';
import * as Sentry from '@sentry/react';
import { setApiEndpoint, setAppInfo } from '@ygoargentina/sdk/api/api.types';
import { ServiceName } from '@ygoargentina/helpers/services';
import { initClarity } from '@ygoargentina/helpers/clarity';
import {
  setupChunkErrorHandler,
  isChunkLoadError,
} from './utils/chunk-error-handler';
import { ChunkErrorBoundary } from './components/check-error-boundary';
import packageJson from '../package.json';

// Configurar el endpoint de la API antes de inicializar cualquier módulo del SDK
setApiEndpoint(Config.apiUrl);
setAppInfo(
  ServiceName.FOLDER,
  packageJson.version,
  packageJson.commitHash
);
// Conecta los avisos del SDK (incluido el error de una mutation que nadie
// manejó) con el <Toaster /> de abajo. Ver YGO-279 / YGO-261.
registerSonnerNotifier();

import { isAuthInitializing } from './utils/auth-init';

// Configure unauthorized handler to clear store
// This prevents infinite loops when legacy tokens are invalid
setUnauthorizedHandler(() => {
  // Skip during initialization - let AuthContext handle it
  if (isAuthInitializing()) {
    return;
  }

  // Clear the user store - React will re-render with authRouter
  useUserStore.getState().setUser(null);
  // Clear CSRF token to prevent stale cookies from causing loops
  authService.clearCsrfToken();
  // Force page reload to reset all state and switch to authRouter
  window.location.reload();
});

// Setup chunk error handler to handle stale chunks after deployments
setupChunkErrorHandler();

// Microsoft Clarity: el gate de produccion vive adentro del helper.
initClarity();

if (Config.isProduction && Config.sentryEnabled) {
  Sentry.init({
    enabled: true,
    environment: Config.env,
    dsn: Config.sentryDsn,
    sendDefaultPii: true,
    integrations: [],
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    // El navegador in-app de Instagram inyecta sus propios scripts
    // (`iabjs://…`) y tiran al salir de la página, cuando el puente nativo ya
    // se cerró. No es código nuestro (CALENDAR-2).
    denyUrls: [/^iabjs:\/\//],
    // Brave en iOS inyecta su billetera y asigna `window.ethereum.selectedAddress`
    // sin chequear que `window.ethereum` exista (CALENDAR-3).
    ignoreErrors: ['Java object is gone', /window\.ethereum/],
    beforeSend(event, hint) {
      const error = hint?.originalException;
      if (error instanceof Error && isChunkLoadError(error)) {
        return null;
      }
      return event;
    },
  });
}

// Create router once (stable reference)
const appRouter = createAppRouter();

// Inner component that uses AuthContext (must be inside AuthProvider)
const AppContent = () => {
  const { isLoading } = useAuth();

  // Show loader while checking authentication
  if (isLoading) {
    return <BaseLoader />;
  }

  return <RouterProvider router={appRouter} />;
};

export const App = () => {
  const queryClient = getQueryClient();

  return (
    <ChunkErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <OrderSocketProvider>
          <FolderCollectionProvider>
            <Suspense fallback={<BaseLoader />}>
              <AppContent />
              <Toaster richColors />
            </Suspense>
          </FolderCollectionProvider>
        </OrderSocketProvider>
      </QueryClientProvider>
    </ChunkErrorBoundary>
  );
};

const rootOptions =
  Config.isProduction && Config.sentryEnabled
    ? {
        onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
          if (error instanceof Error && isChunkLoadError(error)) return;
          console.warn('Uncaught error', error, errorInfo.componentStack);
        }),
        onCaughtError: Sentry.reactErrorHandler((error) => {
          if (error instanceof Error && isChunkLoadError(error)) return;
        }),
        onRecoverableError: Sentry.reactErrorHandler((error) => {
          if (error instanceof Error && isChunkLoadError(error)) return;
        }),
      }
    : {};

ReactDOM.createRoot(document.getElementById('root')!, rootOptions).render(
  <React.StrictMode>
    <ThemeProvider defaultTheme="light" storageKey={Config.sessionKey}>
      <AuthProvider>
        <App />
      </AuthProvider>
    </ThemeProvider>
  </React.StrictMode>
);
